import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/app/providers/ToastContext";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { listObjects, updateObject } from "@/lib/api/endpoints/objects";
import { ifMatchFromObject } from "@/lib/etag";
import { apiGet } from "@/lib/api/client";
import { getWatchlist } from "@/lib/api/endpoints/profile-interview";
import { queryKeys } from "@/lib/query/keys";
import type { BriefingEvent, BriefingTask, BriefingWatchItem } from "./BriefingCard";

const ms = (s?: string | null) => (s ? new Date(s).getTime() : NaN);

// Demo-seeded rows carry a `demo-` source prefix. The briefing is always real
// data — never demo — regardless of the app's demo-view toggle (per "no demo in chat").
const isDemoSourced = (o: unknown): boolean => {
  const r = (o ?? {}) as { attributes?: { source?: unknown }; source?: unknown };
  const s = r.attributes?.source ?? r.source;
  return typeof s === "string" && s.startsWith("demo-");
};

// Live prices: CoinGecko is the only no-key, CORS-open feed, so we cover crypto
// here. Stocks/commodities/indices (Gold, S&P, Oil, 10-yr, MSTR) need a keyed
// market feed behind the API — a backend follow-up (spec "data notes").
const CG_IDS: Record<string, string> = {
  bitcoin: "bitcoin", btc: "bitcoin",
  ethereum: "ethereum", eth: "ethereum",
  solana: "solana", sol: "solana",
  dogecoin: "dogecoin", doge: "dogecoin",
  cardano: "cardano", ada: "cardano",
  ripple: "ripple", xrp: "ripple",
  litecoin: "litecoin", ltc: "litecoin",
};
const cgIdFor = (label: string, note?: string): string | undefined =>
  CG_IDS[label.trim().toLowerCase()] ?? (note ? CG_IDS[note.trim().toLowerCase()] : undefined);

/** Thin a long price series down to ~n points for a compact sparkline. */
function downsample(arr: number[], n = 24): number[] {
  if (arr.length <= n) return arr;
  const step = arr.length / n;
  return Array.from({ length: n }, (_, i) => arr[Math.floor(i * step)]);
}

interface CgQuote {
  id: string;
  symbol: string;
  current_price: number;
  price_change_percentage_24h: number;
  sparkline_in_7d?: { price: number[] };
}

// API flattens attributes to top-level on reads, but keep both paths safe.
const attrs = (o: unknown): Record<string, unknown> => {
  const r = (o ?? {}) as { attributes?: Record<string, unknown> } & Record<string, unknown>;
  return { ...(r.attributes ?? {}), ...r };
};

/**
 * Assembles the three Briefing Card feeds from live workspace data:
 * today's calendar events, open tasks, and the curated watchlist. Cached via
 * TanStack Query (60s) so opening the Briefing tab is instant after the first load.
 */
export function useBriefingData({ includeWatchlist = false }: { includeWatchlist?: boolean } = {}) {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const qc = useQueryClient();
  const { showToast } = useToast();
  // Snapshot the clock once per mount — a fresh Date() each render would bust
  // every downstream useMemo (this card lives in the always-re-rendering chat).
  const now = useMemo(() => new Date(), []);
  const STALE = 60_000;

  const eventsKey = queryKeys.synthesis.tab(ws, "briefing-events");
  const tasksKey = queryKeys.synthesis.tab(ws, "briefing-tasks");
  const watchKey = queryKeys.synthesis.tab(ws, "briefing-watch");

  const eventsQ = useQuery({
    queryKey: eventsKey,
    queryFn: async () => {
      // /events expands recurring occurrences server-side (default) within the
      // window — so a weekly event (e.g. "bridge") shows today, unlike
      // listObjects(type:event) which returns only the (past-dated) master.
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      // Fetch a 90-day window so the card can switch Today / 7 / 30 / 90 days
      // client-side without re-fetching per tab.
      const windowEnd = new Date(now);
      windowEnd.setDate(windowEnd.getDate() + 90);
      windowEnd.setHours(23, 59, 59, 999);
      const search = new URLSearchParams({
        startAfter: dayStart.toISOString(),
        startBefore: windowEnd.toISOString(),
        limit: "500",
      });
      const { data } = await apiGet<{ items?: unknown[] }>(`/events?${search.toString()}`);
      return data;
    },
    enabled: Boolean(token),
    staleTime: STALE,
  });
  const tasksQ = useQuery({
    queryKey: tasksKey,
    queryFn: () => listObjects(token ?? null, { type: "task", limit: 200 }),
    enabled: Boolean(token),
    staleTime: STALE,
  });
  const watchQ = useQuery({
    queryKey: watchKey,
    queryFn: () => getWatchlist(token ?? null, ws),
    // Only fetch the watchlist (and its CoinGecko prices below) when a consumer
    // actually renders it — the briefing card has it separated out by default.
    enabled: Boolean(token) && includeWatchlist,
    staleTime: STALE,
  });

  // Curated watchlist rows; resolve crypto ids for live pricing.
  const watchRaw = (watchQ.data?.items ?? []) as Array<{
    id: string;
    label: string;
    category?: string;
    note?: string;
  }>;
  const cgIds = Array.from(
    new Set(
      watchRaw
        .filter((it) => it.category === "price")
        .map((it) => cgIdFor(it.label, it.note))
        .filter(Boolean) as string[],
    ),
  );
  const pricesQ = useQuery({
    queryKey: [...watchKey, "cg", cgIds.slice().sort().join(",")],
    queryFn: async (): Promise<CgQuote[]> => {
      const res = await fetch(
        `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${cgIds.join(",")}&sparkline=true&price_change_percentage=24h`,
      );
      return res.ok ? ((await res.json()) as CgQuote[]) : [];
    },
    enabled: Boolean(token) && includeWatchlist && cgIds.length > 0,
    staleTime: STALE,
  });
  const cgMap = new Map((pricesQ.data ?? []).map((c) => [c.id, c]));

  const eventsRaw: BriefingEvent[] = ((eventsQ.data?.items ?? []) as unknown[])
    .filter((o) => !isDemoSourced(o))
    .map((o) => {
      const a = attrs(o);
      const oo = o as {
        id?: string;
        title?: string;
        rrule?: unknown;
        recurrence?: unknown;
        recurringEventId?: unknown;
        _isVirtual?: unknown;
      };
      const att = Array.isArray(a.attendees) ? (a.attendees as Array<Record<string, unknown>>) : [];
      // Recurrence frequency from the RRULE (e.g. "FREQ=WEEKLY"). Annual stays;
      // weekly/daily/monthly are routine and the card hides them.
      const rrule = String(oo.rrule ?? a.rrule ?? (Array.isArray(oo.recurrence) ? oo.recurrence.join(";") : oo.recurrence ?? a.recurrence ?? ""));
      const freq = /FREQ=([A-Z]+)/i.exec(rrule)?.[1]?.toLowerCase();
      const recurringFreq =
        freq === "yearly" || freq === "monthly" || freq === "weekly" || freq === "daily"
          ? (freq as "yearly" | "monthly" | "weekly" | "daily")
          : freq
            ? ("other" as const)
            : oo.recurringEventId || oo._isVirtual
              ? ("other" as const)
              : undefined;
      return {
        id: String(oo.id ?? ""),
        title: String(oo.title ?? a.title ?? "Untitled"),
        start: (a.startAt as string) ?? (a.startTime as string) ?? null,
        end: (a.endAt as string) ?? (a.endTime as string) ?? null,
        allDay: a.allDay === true,
        attendees: att.map((x) => String(x.name ?? x.displayName ?? x.email ?? "")).filter(Boolean),
        recurringFreq,
      };
    })
    // No date filter here — the hook returns the full 90-day window; the card
    // scopes to the selected horizon (Today / 7 / 30 / 90) and curates.
    .sort((x, y) => ms(x.start) - ms(y.start));

  // Dedupe cross-account / re-synced copies: same title on the same day (e.g. a
  // flight that exists in two connected Google accounts, where the two copies
  // can carry slightly different timestamps). Keep the first. Title normalization
  // strips punctuation and per-word trailing 's' so possessive variants collapse
  // ("vincent's birthday" ≡ "Vincent Birthday").
  const dayKey = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
  const titleKey = (title: string) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => (w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w))
      .map((w) => (w === "bday" ? "birthday" : w))
      .join(" ");
  const events: BriefingEvent[] = [];
  const seenEventKeys = new Set<string>();
  for (const e of eventsRaw) {
    const key = `${titleKey(e.title || "")}|${dayKey(e.start)}`;
    if (seenEventKeys.has(key)) continue;
    seenEventKeys.add(key);
    events.push(e);
  }

  const tasks: BriefingTask[] = ((tasksQ.data?.items ?? []) as unknown[])
    .filter((o) => !isDemoSourced(o))
    .map((o) => {
    const a = attrs(o);
    return {
      id: String((o as { id?: string }).id ?? ""),
      title: String((o as { title?: string }).title ?? a.title ?? "Untitled"),
      due: (a.dueAt as string) ?? null,
      done: (o as { status?: string }).status === "done" || a.done === true,
    };
  })
    // Briefing = "what's on your plate": only dated tasks. Drops undated project
    // labels like "Email Campaigns / X", "Google Drive" that aren't real to-dos.
    .filter((t) => t.due != null);

  const watchlist: BriefingWatchItem[] = watchRaw
    // Omit news/topic rows — there's no fresh-news feed yet, so don't list bare
    // topic names ("if no new news, just omit"). Show priced tickers only.
    .filter((it) => it.category === "price")
    .map((it) => {
      const id = cgIdFor(it.label, it.note);
      const q = id ? cgMap.get(id) : undefined;
      return {
        kind: "ticker" as const,
        id: it.id,
        name: it.label,
        symbol: q?.symbol?.toUpperCase() ?? it.note ?? undefined,
        price: q ? `$${Math.round(q.current_price).toLocaleString()}` : undefined,
        deltaPct: q ? Math.round((q.price_change_percentage_24h ?? 0) * 10) / 10 : undefined,
        spark: q?.sparkline_in_7d?.price ? downsample(q.sparkline_in_7d.price) : undefined,
      };
    });

  // Mark a task done from the card; optimistic-ish via cache invalidation.
  const toggleTask = (id: string) => {
    // Tasks track completion via the top-level `status` field (mirrors
    // TaskListModule), not an attributes.done flag. If-Match from the cached
    // row's version so a concurrent task edit 412s instead of being clobbered;
    // the unconditional invalidation below refetches fresh data either way. A
    // failure invalidates too but is no longer silent — it surfaces a toast (#5283).
    const row = ((tasksQ.data?.items ?? []) as Array<{ id?: string; version?: number; _etag?: string }>)
      .find((o) => o.id === id);
    void updateObject(token ?? null, "task", id, { status: "done" } as never, ifMatchFromObject(row))
      .catch(() => showToast("Couldn't mark that task done — try again.", "error"))
      .finally(() => void qc.invalidateQueries({ queryKey: tasksKey }));
  };

  // Force-refresh all three feeds (the card's refresh button).
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: eventsKey });
    void qc.invalidateQueries({ queryKey: tasksKey });
    void qc.invalidateQueries({ queryKey: watchKey });
  };

  return {
    events,
    tasks,
    watchlist,
    now,
    toggleTask,
    refresh,
    isLoading: eventsQ.isLoading || tasksQ.isLoading || (includeWatchlist && watchQ.isLoading),
    // Any feed failing means the card's empty sections may be lies — surface it
    // so the panel can offer a retry instead of a confident "nothing today" (#5283).
    isError: eventsQ.isError || tasksQ.isError || (includeWatchlist && watchQ.isError),
  };
}
